//Angular
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

// 3rd Party dependencies
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

//Zonar Pattern Library
import { ZonarUICoreModule } from '@zonar-ui/core';
import { ZonarUIMaterialModule } from '@zonar-ui/material';
import { ZonarUiFooterModule } from '@zonar-ui/footer';
import { ZonarUiProgressSpinnerModule } from '@zonar-ui/progress-spinner';
import { ZonarOwnerIdInterceptor, ZonarUiAuthModule, ZonarUiPermissionsModule } from '@zonar-ui/auth';
import { I18nService, TranslateService, ZonarUiI18nModule } from '@zonar-ui/i18n';
import { SidenavModule } from '@zonar-ui/sidenav';
import { ZonarUiMapModule} from '@zonar-ui/zonar-ui-map';
import {DropdownComponent} from '@zonar-ui/filter';

// App-level Imports
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from '@app/modules/core/core.module';
import { ResourceLoadState } from '@app/models/resource-load.state';
import { Translations } from '@app/services/i18n/translations.service';
import { environment } from '@environments/environment';
import { AppComponent } from './app.component';
import { ManageZonesPageComponent } from './pages/manage-zones/manage-zones-page.component';
import { ZonesDrawerComponent } from './pages/manage-zones/zones-drawer/zones-drawer.component';
import { MapWrapperComponent } from './pages/manage-zones/map-wrapper/map-wrapper.component';
import { CreateZoneComponent } from './pages/manage-zones/zones-drawer/create-zone/create-zone.component';
import { ZoneEditorComponent } from './pages/manage-zones/zones-drawer/zone-editor/zone-editor.component';
import { FilterDataAsyncLoadingPipe } from './pages/manage-zones/zones-drawer/pipes/filter-data-async-loading.pipe';
import { DialogComponent } from './components/dialog/dialog.component';

export function loadAppTranslations(
  translateService: TranslateService,
  i18nService: I18nService,
  translations: Translations
): () => Observable<any> {
  return () => {
    return translateService
      .use(
        i18nService.getLanguageInUse()
      )
      .pipe(
        tap(() => {
          translateService.setDefaultLang(environment.i18n.defaultLanguage);
          translations.translationsLoadState.next(ResourceLoadState.LOAD_SUCCESSFUL);
        }),
        switchMap((lang) => of(lang))
      );
  };
}

@NgModule({
  declarations: [
    AppComponent,
    ManageZonesPageComponent,
    ZonesDrawerComponent,
    CreateZoneComponent,
    DialogComponent
  ],
  imports: [
    AppRoutingModule,
    AuthModule.forRoot({
      audience: environment.auth.audience,
      clientId: environment.auth.clientID,
      domain: environment.auth.domain,
      redirect_uri: window.location.origin,
      scope: 'openid profile email',
      max_age: 36000,
      useRefreshTokens: environment.auth.useRefreshTokens,
      cacheLocation: 'localstorage',
      httpInterceptor: {
        allowedList: [
          `${environment.apiBase.url}/*`,
          `${environment.coreEntityApiBase.url}/*`,
          /* allowing beta2 is necessary for apis on beta versions (e.g., settings) */
          `${environment.coreEntityApiBase.url}beta2/*`,
          `${environment.userPreferenceApiBase.url}/*`,
        ],
      },
    }),
    BrowserAnimationsModule,
    BrowserModule,
    ReactiveFormsModule,
    ZonarUiAuthModule,
    ZonarUiPermissionsModule,
    ZonarUIMaterialModule,
    CoreModule,
    FlexLayoutModule,
    HttpClientModule,
    NgxSkeletonLoaderModule,
    SidenavModule.forRoot({ stage: environment.authEnv || null }),
    ZonarUICoreModule,
    ZonarUiI18nModule,
    ZonarUiProgressSpinnerModule,
    ZonarUiFooterModule,
    ZonarUiMapModule.forRoot({
      hereApiKey: environment.apiKeyV3,
      usingV3HereApi: true
    }),
    MapWrapperComponent,
    DropdownComponent,
    FilterDataAsyncLoadingPipe,
    ZoneEditorComponent,
  ],
  exports: [
    FilterDataAsyncLoadingPipe,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ZonarOwnerIdInterceptor, multi: true },

    { provide: APP_INITIALIZER, useFactory: loadAppTranslations, deps: [TranslateService, I18nService, Translations], multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
